import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { AnimatedSection } from '../components/AnimatedSection';
import { useAuth, useProfile } from '../context';
import { User, Mail, Lock, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

const emailSchema = yup.object().shape({
  email: yup.string().email('Invalid email format').nullable().default(null),
});

const passwordSchema = yup
  .object({
    current_password: yup.string().required('Current password is required'),
    new_password: yup
      .string()
      .min(6, 'Password must be at least 6 characters')
      .required('New password is required'),
    confirm_password: yup
      .string()
      .oneOf([yup.ref('new_password')], 'Passwords must match')
      .required('Please confirm your password'),
  })
  .required();

type EmailFormValues = yup.InferType<typeof emailSchema>;
type PasswordFormValues = yup.InferType<typeof passwordSchema>;

export const SettingsPage = () => {
  const { user } = useAuth();
  const { profile, loading, error, fetchProfile, updateProfile, changePassword, clearError } = useProfile();
  const [updateSuccess, setUpdateSuccess] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  useEffect(() => {
    void fetchProfile();
  }, [fetchProfile]);

  const {
    register: registerEmail,
    handleSubmit: handleSubmitEmail,
    formState: { errors: emailErrors, isSubmitting: isEmailSubmitting },
    reset: resetEmail,
  } = useForm<EmailFormValues>({
    resolver: yupResolver(emailSchema),
    defaultValues: {
      email: profile?.email || '',
    },
  });

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: passwordErrors, isSubmitting: isPasswordSubmitting },
    reset: resetPassword,
  } = useForm<PasswordFormValues>({
    resolver: yupResolver(passwordSchema),
    defaultValues: {
      current_password: '',
      new_password: '',
      confirm_password: '',
    },
  });

  useEffect(() => {
    if (profile) {
      resetEmail({ email: profile.email || '' });
    }
  }, [profile, resetEmail]);

  const onUpdateEmail = async (values: EmailFormValues) => {
    clearError();
    setUpdateSuccess(null);

    try {
      await updateProfile({ email: values.email || undefined });
      setUpdateSuccess('Email updated successfully!');
      setTimeout(() => setUpdateSuccess(null), 3000);
    } catch (err) {
      // Error is handled by context
    }
  };

  const onChangePassword = async (values: PasswordFormValues) => {
    clearError();
    setPasswordSuccess(null);

    try {
      await changePassword(values.current_password, values.new_password);
      setPasswordSuccess('Password changed successfully!');
      resetPassword({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
      setTimeout(() => setPasswordSuccess(null), 3000);
    } catch (err) {
      // Error is handled by context
    }
  };

  if (loading && !profile) {
    return (
      <AnimatedSection className="section-padding">
        <div className="mx-auto flex w-full max-w-4xl items-center justify-center py-20">
          <div className="flex flex-col items-center gap-3">
            <div className="h-12 w-12 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
            <p className="text-sm font-medium text-slate-500 dark:text-slate-300">Loading your profile...</p>
          </div>
        </div>
      </AnimatedSection>
    );
  }

  return (
    <AnimatedSection className="section-padding">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-8">
        <div>
          <h1 className="font-display text-3xl text-slate-900 dark:text-white">Account Settings</h1>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            Manage your profile information and security settings
          </p>
        </div>

        {/* Profile Information Card */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center gap-3 border-b border-slate-200 pb-4 dark:border-slate-700">
            <div className="rounded-full bg-brand-100 p-3 dark:bg-brand-900/30">
              <User className="h-6 w-6 text-brand-600 dark:text-brand-300" />
            </div>
            <div>
              <h2 className="font-display text-xl text-slate-900 dark:text-white">Profile Information</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">Your personal details</p>
            </div>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs uppercase tracking-[0.25em] text-slate-400">Full Name</label>
              <p className="mt-1 font-semibold text-slate-900 dark:text-white">{user?.full_name || '—'}</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.25em] text-slate-400">ID Number</label>
              <p className="mt-1 font-semibold text-slate-900 dark:text-white">{user?.id_number || '—'}</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.25em] text-slate-400">Phone Number</label>
              <p className="mt-1 font-semibold text-slate-900 dark:text-white">{user?.phone || '—'}</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.25em] text-slate-400">Status</label>
              <p className="mt-1">
                <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-500/10 dark:text-emerald-200">
                  {user?.status || '—'}
                </span>
              </p>
            </div>
          </div>

          {profile?.reason_for_joining && (
            <div className="mt-4">
              <label className="text-xs uppercase tracking-[0.25em] text-slate-400">Reason for Joining</label>
              <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">{profile.reason_for_joining}</p>
            </div>
          )}
        </div>

        {/* Email Settings Card */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center gap-3 border-b border-slate-200 pb-4 dark:border-slate-700">
            <div className="rounded-full bg-blue-100 p-3 dark:bg-blue-900/30">
              <Mail className="h-6 w-6 text-blue-600 dark:text-blue-300" />
            </div>
            <div>
              <h2 className="font-display text-xl text-slate-900 dark:text-white">Email Address</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">Update your email for notifications</p>
            </div>
          </div>

          <form onSubmit={handleSubmitEmail(onUpdateEmail)} className="mt-6 space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-slate-700 dark:text-slate-200">
                Email
              </label>
              <input
                id="email"
                type="email"
                placeholder="your.email@example.com"
                className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-brand-500 focus:ring focus:ring-brand-200/60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-brand-300"
                {...registerEmail('email')}
              />
              {emailErrors.email && <p className="mt-2 text-sm text-red-500">{emailErrors.email.message}</p>}
            </div>

            <button
              type="submit"
              disabled={isEmailSubmitting}
              className="inline-flex items-center justify-center rounded-full bg-brand-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-brand-400"
            >
              {isEmailSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Email'
              )}
            </button>

            {updateSuccess && (
              <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200">
                <CheckCircle2 className="h-4 w-4" />
                {updateSuccess}
              </div>
            )}
          </form>
        </div>

        {/* Password Change Card */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center gap-3 border-b border-slate-200 pb-4 dark:border-slate-700">
            <div className="rounded-full bg-amber-100 p-3 dark:bg-amber-900/30">
              <Lock className="h-6 w-6 text-amber-600 dark:text-amber-300" />
            </div>
            <div>
              <h2 className="font-display text-xl text-slate-900 dark:text-white">Change Password</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">Update your password regularly for security</p>
            </div>
          </div>

          <form onSubmit={handleSubmitPassword(onChangePassword)} className="mt-6 space-y-4">
            <div>
              <label
                htmlFor="current_password"
                className="block text-sm font-semibold text-slate-700 dark:text-slate-200"
              >
                Current Password
              </label>
              <input
                id="current_password"
                type="password"
                autoComplete="current-password"
                className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-brand-500 focus:ring focus:ring-brand-200/60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-brand-300"
                {...registerPassword('current_password')}
              />
              {passwordErrors.current_password && (
                <p className="mt-2 text-sm text-red-500">{passwordErrors.current_password.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="new_password" className="block text-sm font-semibold text-slate-700 dark:text-slate-200">
                New Password
              </label>
              <input
                id="new_password"
                type="password"
                autoComplete="new-password"
                className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-brand-500 focus:ring focus:ring-brand-200/60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-brand-300"
                {...registerPassword('new_password')}
              />
              {passwordErrors.new_password && (
                <p className="mt-2 text-sm text-red-500">{passwordErrors.new_password.message}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="confirm_password"
                className="block text-sm font-semibold text-slate-700 dark:text-slate-200"
              >
                Confirm New Password
              </label>
              <input
                id="confirm_password"
                type="password"
                autoComplete="new-password"
                className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-brand-500 focus:ring focus:ring-brand-200/60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-brand-300"
                {...registerPassword('confirm_password')}
              />
              {passwordErrors.confirm_password && (
                <p className="mt-2 text-sm text-red-500">{passwordErrors.confirm_password.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isPasswordSubmitting}
              className="inline-flex items-center justify-center rounded-full bg-brand-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-brand-400"
            >
              {isPasswordSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Changing...
                </>
              ) : (
                'Change Password'
              )}
            </button>

            {passwordSuccess && (
              <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200">
                <CheckCircle2 className="h-4 w-4" />
                {passwordSuccess}
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}
          </form>
        </div>
      </div>
    </AnimatedSection>
  );
};

